import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { TwcgOnboardingIntroAnimation } from '@atlassian/twcg-onboarding-intro-animation/animation';

import { isConfluenceWithoutJiraEnabled } from '../../common/is-confluence-without-jira-enabled';
import { useTwcProductSplit } from '../../common/use-twc-product-split';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';

/**
 * Products the intro animation has a dedicated beat for, in the order it plays them.
 */
const INTRO_ANIMATION_PRODUCTS = ['jira', 'confluence', 'loom', 'rovo'] as const;

export type IntroAnimationProduct = (typeof INTRO_ANIMATION_PRODUCTS)[number];

type Props = {
	onComplete: () => void;
};

/**
 * The animated variant of the post-provisioning welcome step.
 *
 * Replaces `WelcomeScreen` when the host selects `welcome-screen-twcg-animation` after provisioning.
 *
 * Unlike `WelcomeScreen` this screen does not rewrite `redirectUrl` for AIFC. Hosts on this path
 * open the first-action experience in place after `onComplete`, so the raw provisioning URL must
 * survive for them to parse the space and content IDs out of.
 */
export const WelcomeScreenTwcgAnimation = ({ onComplete }: Props): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		currentUserProfile: profile,
		modalState,
		sourceComponent,
		sourceContext,
	} = useModalContext();
	const { newProducts } = useTwcProductSplit(modalState);
	const showJiraBeat = isConfluenceWithoutJiraEnabled(modalState.userProducts);

	// Filter the animation's own ordered list rather than the split, so the result stays narrowed to
	// the products the animation can actually render, in its canonical order.
	const animationProducts = INTRO_ANIMATION_PRODUCTS.filter(
		(product) => (product !== 'jira' || showJiraBeat) && newProducts.includes(product),
	);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalWelcomeScreenTwcgAnimation',
			attributes: {
				// Record the products that determine the animation's length and beats.
				newProducts: animationProducts,
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per screen mount; the derived attributes are stable for the life of the screen
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	return (
		<ScreenLayout testId="welcome-screen-twcg-animation">
			<TwcgOnboardingIntroAnimation
				avatarSources={profile?.avatarUrl ? { you: profile.avatarUrl } : undefined}
				hideSkip
				layout="modal"
				newProducts={animationProducts}
				onComplete={onComplete}
				userName={profile?.firstName}
			/>
		</ScreenLayout>
	);
};
